type WebNavigationDirection = -1 | 0 | 1;

/** Plays the focused rise layer's exit, then reports completion. */
export type WebRiseLayerDismissal = (onExitComplete: () => void) => void;

export type WebRiseDismissalReplayGate = {
  cancel: () => void;
  replayIfPending: (replay: () => void) => void;
  tryBegin: () => boolean;
};

type MotionStore = {
  direction: WebNavigationDirection;
  traversalPending: boolean;
  hintedDirection: WebNavigationDirection | null;
  fadeReveal: boolean;
  stationaryIntentPending: boolean;
  stationaryTraversalPending: boolean;
  riseLayerDismissals: WebRiseLayerDismissal[];
};

// One store per JS runtime, not per module instance: dev-server hot reloads
// re-execute this module, and a forked store would leave stale listeners
// writing state the UI no longer reads.
const globalScope = globalThis as { __recitalNavMotionStore?: MotionStore };
const store: MotionStore =
  (globalScope.__recitalNavMotionStore ??= {
    direction: 0,
    traversalPending: false,
    hintedDirection: null,
    fadeReveal: false,
    stationaryIntentPending: false,
    stationaryTraversalPending: false,
    riseLayerDismissals: []
  });

// Additive store fields need defaults when Fast Refresh preserves a store
// created by an older version of this module.
store.fadeReveal ??= false;
store.stationaryIntentPending ??= false;
store.stationaryTraversalPending ??= false;
store.riseLayerDismissals ??= [];

/**
 * Dispatch one web navigation as stationary. This is intentionally a
 * one-shot escape hatch for dismissing a layer whose destination was never
 * visually displaced; ordinary Back actions remain backward-directed.
 */
export function dispatchWebNavigationWithoutDirection(dispatch: () => void): void {
  store.stationaryIntentPending = true;
  try {
    dispatch();
  } catch (error) {
    store.stationaryIntentPending = false;
    throw error;
  }
}

/**
 * Declare the semantic direction of an imminent router action whose type
 * cannot express it — e.g. a header Back press that falls back to replace()
 * because a refreshed tab has no stack to pop. The next recorded action
 * consumes it; only REPLACE uses it (explicit actions already carry their
 * own direction).
 */
export function hintWebNavigationDirection(next: WebNavigationDirection): void {
  store.hintedDirection = next;
}

/**
 * React Navigation marks the outgoing web scene aria-hidden as soon as a
 * route action commits. Release a clicked control before that happens so
 * focus is never retained inside the newly hidden scene.
 */
export function releaseWebNavigationFocus(): void {
  if (typeof document === "undefined" || typeof HTMLElement === "undefined") return;
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && activeElement !== document.body) {
    activeElement.blur();
  }
}

/**
 * Level-based truth about the focused screen: while a raised layer (a
 * "rise" screen such as Account) holds focus, any backward browser
 * traversal dismisses it — the destination beneath must reveal in place,
 * and the layer's own exit must play BEFORE the router removes it.
 * This deliberately replaces an earlier edge-triggered "arm on focus"
 * one-shot, which raced Expo Router's trailing PUSH write on the layer's
 * very first mount. Registration is level-based (register on focus,
 * unregister on blur), so router writes cannot clobber it, and at
 * popstate time the blur has not happened yet — the layer is still
 * registered when direction is decided.
 *
 * The registered dismissal exists because usePreventRemove is unreliable
 * for BROWSER traversals: expo-router replays them as resetRoot with a
 * recorded state, and when that reset targets the root navigator the
 * nested stack is replaced wholesale — no beforeRemove ever fires and the
 * layer would snap out. The popstate interceptor in navigationMotion.ts
 * calls this dismissal instead, before the router hears about it.
 */
export function registerWebRiseLayer(dismiss: WebRiseLayerDismissal): () => void {
  // Give every registration its own identity. The same dismissal function
  // may be registered more than once, and either unregister callback must
  // remain idempotent regardless of cleanup order.
  const registration: WebRiseLayerDismissal = (onExitComplete) =>
    dismiss(onExitComplete);
  store.riseLayerDismissals.push(registration);
  return () => {
    const index = store.riseLayerDismissals.indexOf(registration);
    if (index >= 0) store.riseLayerDismissals.splice(index, 1);
  };
}

/** The focused (most recently registered) rise layer's dismissal, if any. */
export function getWebFocusedRiseLayerDismissal(): WebRiseLayerDismissal | null {
  return store.riseLayerDismissals[store.riseLayerDismissals.length - 1] ?? null;
}

/**
 * Gate one withheld popstate replay. A second real traversal cancels the
 * pending replay so its older history entry can never be re-applied later.
 */
export function createWebRiseDismissalReplayGate(): WebRiseDismissalReplayGate {
  let pending = false;
  return {
    cancel() {
      pending = false;
    },
    replayIfPending(replay) {
      if (pending) replay();
    },
    tryBegin() {
      if (pending) return false;
      pending = true;
      return true;
    }
  };
}

/**
 * Direction comes from two authoritative sources, never from guessing at
 * history contents:
 * - Router intent: expo-router's dispatched action, for in-app navigation.
 * - Browser traversal: the signed history-index delta observed on popstate
 *   (see navigationMotion.ts for the index stamping).
 */
export function recordWebNavigationIntent(actionType: string): void {
  const hint = store.hintedDirection;
  store.hintedDirection = null;
  if (store.stationaryIntentPending) {
    store.stationaryIntentPending = false;
    store.direction = 0;
    store.fadeReveal = true;
    store.traversalPending = false;
    store.stationaryTraversalPending =
      actionType === "GO_BACK" ||
      actionType === "POP" ||
      actionType === "POP_TO" ||
      actionType === "POP_TO_TOP";
    return;
  }
  switch (actionType) {
    case "PUSH":
    case "NAVIGATE":
      store.direction = 1;
      store.fadeReveal = false;
      store.traversalPending = false;
      store.stationaryTraversalPending = false;
      return;
    case "GO_BACK":
    case "POP":
    case "POP_TO":
    case "POP_TO_TOP":
      store.direction = -1;
      store.fadeReveal = false;
      store.traversalPending = false;
      store.stationaryTraversalPending = false;
      return;
    case "REPLACE":
      store.direction = hint ?? 0;
      store.fadeReveal = false;
      store.traversalPending = false;
      store.stationaryTraversalPending = false;
      return;
    case "RESET":
      // Browser Back/Forward reaches the router as a RESET, moments after
      // the popstate that already recorded the true direction — keep it.
      // Any other reset is a fresh baseline with no travel.
      if (store.traversalPending) store.traversalPending = false;
      else {
        store.direction = 0;
        store.fadeReveal = false;
      }
      return;
    default:
      return;
  }
}

/**
 * Record browser history traversal by its signed index delta. Null means the
 * destination entry carries no stamp (created outside this app), which reads
 * as stationary rather than guessing.
 */
export function recordWebHistoryTraversal(indexDelta: number | null): void {
  store.traversalPending = true;
  if (store.stationaryTraversalPending) {
    store.stationaryTraversalPending = false;
    store.direction = 0;
  } else if (
    indexDelta != null &&
    indexDelta < 0 &&
    store.riseLayerDismissals.length > 0
  ) {
    // Browser Back while a raised layer is focused: the layer plays its own
    // reverse-rise exit; the page beneath was never displaced, so it fades
    // back in place instead of sliding.
    store.direction = 0;
    store.fadeReveal = true;
  } else {
    store.direction = !indexDelta ? 0 : indexDelta > 0 ? 1 : -1;
    store.fadeReveal = false;
  }
}

/**
 * Non-destructive read: every focused consumer of the same navigation sees
 * the same direction, however many times focus effects replay.
 */
export function getWebNavigationDirection(): WebNavigationDirection {
  return store.direction;
}

/** Whether this stationary navigation is the reveal half of a layer dismissal. */
export function getWebNavigationFadeReveal(): boolean {
  return store.fadeReveal;
}
